import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/ui/LoadingSpinner';

/**
 * This component serves as a redirect page to prevent WhatsApp from generating previews
 * for fallback response pages. It immediately redirects to the actual fallback response page.
 */
function FallbackRedirect() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    // Add a small delay to ensure the redirect happens after the page loads
    const timer = setTimeout(() => {
      navigate(`/fallback-response/${id}`, { replace: true });
    }, 100);

    return () => clearTimeout(timer);
  }, [id, navigate]);

  // Add meta tags to prevent link previews
  useEffect(() => {
    // Create meta tags to prevent link previews
    const metaTags = [
      { name: 'robots', content: 'noindex, nofollow' },
      { name: 'description', content: '' },
      { property: 'og:title', content: 'Redirecionando...' },
      { property: 'og:description', content: '' },
      { property: 'og:image', content: '' },
      { name: 'twitter:card', content: 'none' },
    ];

    // Add meta tags to head
    const addedTags = metaTags.map(tagInfo => {
      const tag = document.createElement('meta');
      Object.entries(tagInfo).forEach(([attr, value]) => {
        tag.setAttribute(attr, value);
      });
      document.head.appendChild(tag);
      return tag;
    });

    // Clean up function to remove added tags
    return () => {
      addedTags.forEach(tag => {
        if (tag.parentNode) {
          tag.parentNode.removeChild(tag);
        }
      });
    };
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
      <div className="text-center">
        <LoadingSpinner size="lg" />
        <p className="mt-4 text-gray-600 dark:text-gray-300">Redirecionando...</p>
      </div>
    </div>
  );
}

export default FallbackRedirect;
